import { ClientExecutive } from '@app/model/ClientExecutive';
import { Country } from '@app/model/Country';
import { Course } from '@app/model/Course';
import { Facility } from '@app/model/Facility';
import { Holiday } from '@app/model/Holiday';
import { Instructor } from '@app/model/Instructor';
import { Klass } from '@app/model/Klass';
import { Language } from '@app/model/Language';
import { Partner } from '@app/model/Partner';
import { PipelineKlass } from '@app/model/PipelineKlass';
import { RedHatRegion } from '@app/model/RedHatRegion';
import { TrainingSpecialistStrategic } from '@app/model/TrainingSpecialistStrategic';

/**
 * Turning the flat records the server sends into records whose references point at the one
 * shared copy of each entity the stores already hold.
 *
 * Pure functions: every dictionary an ingestor resolves against arrives as an argument, and
 * nothing here imports a store. That is deliberate. entityStore.ts needs these functions at
 * module-evaluation time to build its stores, so a reference back the other way is a cycle,
 * and in a cycle whichever module is evaluated second sees the other's exports as undefined.
 * Here that meant `createEntityStore` being handed an undefined ingestor and quietly falling
 * back to its identity default — no error, just klasses that are never hydrated.
 *
 * The caller reads the dictionaries at ingest time (`useXStore.getState().items`), so they are
 * exactly as fresh as they were when the ingestor read them itself.
 */

// Helper function to generate fast lookup dictionaries
const buildCacheMap = <T extends { id: number | string }>(storeItems: T[]) =>
  new Map(storeItems.map(item => [item.id, item]));

// ----------------------------------------------------
// 1. INGEST KLASSES (The heaviest entity)
// ----------------------------------------------------
export interface KlassDictionaries {
  instructors: Instructor[];
  facilities: Facility[];
  languages: Language[];
  countries: Country[];
  courses: Course[];
  clientExecutives: ClientExecutive[];
  tss: TrainingSpecialistStrategic[];
}

export const ingestKlasses = (rawKlasses: Klass[], dictionaries: KlassDictionaries): Klass[] => {
  const instructors = buildCacheMap(dictionaries.instructors);
  const facilities = buildCacheMap(dictionaries.facilities);
  const languages = buildCacheMap(dictionaries.languages);
  const countries = buildCacheMap(dictionaries.countries);
  const courses = buildCacheMap(dictionaries.courses);
  const clientExecs = buildCacheMap(dictionaries.clientExecutives);
  const tsses = buildCacheMap(dictionaries.tss);

  const hydrate = (list: Instructor[] | undefined) =>
    list?.map(inst => (inst?.id ? instructors.get(inst.id) || inst : inst));

  return rawKlasses.map(klass => {
    // Instructor lists
    if (klass.instructors?.length) klass.instructors = hydrate(klass.instructors)!;
    if (klass.allocatedInstructors?.length) klass.allocatedInstructors = hydrate(klass.allocatedInstructors)!;
    if (klass.finalInstructors?.length) klass.finalInstructors = hydrate(klass.finalInstructors)!;

    // Single Object References
    if (klass.facility?.id) klass.facility = facilities.get(klass.facility.id) || klass.facility;
    if (klass.language?.id) klass.language = languages.get(klass.language.id) || klass.language;
    if (klass.country?.id) klass.country = countries.get(klass.country.id) || klass.country;
    if (klass.course?.id) klass.course = courses.get(klass.course.id) || klass.course;
    if (klass.clientExecutive?.id) klass.clientExecutive = clientExecs.get(klass.clientExecutive.id) || klass.clientExecutive;
    if (klass.tss?.id) klass.tss = tsses.get(klass.tss.id) || klass.tss;

    return klass;
  });
};

// ----------------------------------------------------
// 1b. INGEST PIPELINE REQUESTS
// ----------------------------------------------------

/**
 * The same dictionaries a class resolves against, less the facilities: a request names a city and a
 * country rather than a room, because there is no class to book one for yet.
 */
export type PipelineKlassDictionaries = Omit<KlassDictionaries, 'facilities'>;

export const ingestPipelineKlasses = (
  rawRequests: PipelineKlass[],
  dictionaries: PipelineKlassDictionaries
): PipelineKlass[] => {
  const instructors = buildCacheMap(dictionaries.instructors);
  const languages = buildCacheMap(dictionaries.languages);
  const countries = buildCacheMap(dictionaries.countries);
  const courses = buildCacheMap(dictionaries.courses);
  const clientExecs = buildCacheMap(dictionaries.clientExecutives);
  const tsses = buildCacheMap(dictionaries.tss);

  return rawRequests.map(request => {
    // One plain list of instructors, not the three stage chain a class carries.
    if (request.instructors?.length) {
      request.instructors = request.instructors.map(inst => (inst?.id ? instructors.get(inst.id) || inst : inst));
    }

    if (request.language?.id) request.language = languages.get(request.language.id) || request.language;
    if (request.country?.id) request.country = countries.get(request.country.id) || request.country;
    if (request.course?.id) request.course = courses.get(request.course.id) || request.course;
    if (request.clientExecutive?.id) request.clientExecutive = clientExecs.get(request.clientExecutive.id) || request.clientExecutive;
    if (request.tss?.id) request.tss = tsses.get(request.tss.id) || request.tss;

    return request;
  });
};

// ----------------------------------------------------
// 2. INGEST INSTRUCTORS
// ----------------------------------------------------
export interface InstructorDictionaries {
  countries: Country[];
  languages: Language[];
  partners: Partner[];
  clientExecutives: ClientExecutive[];
}

export const ingestInstructors = (rawInstructors: Instructor[], dictionaries: InstructorDictionaries): Instructor[] => {
  const countries = buildCacheMap(dictionaries.countries);
  const languages = buildCacheMap(dictionaries.languages);
  const partners = buildCacheMap(dictionaries.partners);
  const clientExecs = buildCacheMap(dictionaries.clientExecutives);

  return rawInstructors.map(inst => {
    // Single Object References
    if (inst.nacionality?.id) inst.nacionality = countries.get(inst.nacionality.id) || inst.nacionality;
    if (inst.passportAuthoriCountry?.id) inst.passportAuthoriCountry = countries.get(inst.passportAuthoriCountry.id) || inst.passportAuthoriCountry;
    if (inst.partner?.id) inst.partner = partners.get(inst.partner.id) || inst.partner;
    if (inst.clientExecutive?.id) inst.clientExecutive = clientExecs.get(inst.clientExecutive.id) || inst.clientExecutive;

    // Array References
    if (inst.languages && inst.languages.length > 0) {
      inst.languages = inst.languages.map(lang => languages.get(lang.id) || lang);
    }

    return inst;
  });
};

// ----------------------------------------------------
// 3. INGEST PARTNERS
// ----------------------------------------------------
export const ingestPartners = (rawPartners: Partner[], dictionaries: { clientExecutives: ClientExecutive[] }): Partner[] => {
  const clientExecs = buildCacheMap(dictionaries.clientExecutives);

  return rawPartners.map(partner => {
    if (partner.clientExecutive?.id) {
      partner.clientExecutive = clientExecs.get(partner.clientExecutive.id) || partner.clientExecutive;
    }
    return partner;
  });
};

// ----------------------------------------------------
// 4. INGEST CLIENT EXECUTIVES
// ----------------------------------------------------
export const ingestClientExecutives = (rawExecs: ClientExecutive[], dictionaries: { redHatRegions: RedHatRegion[] }): ClientExecutive[] => {
  const regions = buildCacheMap(dictionaries.redHatRegions);

  return rawExecs.map(exec => {
    if (exec.regions && exec.regions.length > 0) {
      exec.regions = exec.regions.map(reg => regions.get(reg.id) || reg);
    }
    return exec;
  });
};

// ----------------------------------------------------
// 5. INGEST HOLIDAYS
// ----------------------------------------------------
export const ingestHolidays = (rawHolidays: Holiday[], dictionaries: { countries: Country[] }): Holiday[] => {
  const countries = buildCacheMap(dictionaries.countries);

  return rawHolidays.map(raw => ({
    ...raw,
    // Safely transforms the date string to YYYY-MM-DD format
    date: (raw.date ? new Date(raw.date).toISOString().split('T')[0] : raw.date) || '',
    country: raw.country?.id ? (countries.get(raw.country.id) || raw.country) : raw.country,
    // ISO 3166-2 is an uppercase code, but the service matches `?region=` case-insensitively and
    // stores what it was given, so the rows can disagree with each other. Everything downstream
    // compares regions - a name lookup, a picker's current selection, the nationwide filter - so
    // they are settled here, in the one place that already normalises a field on the way in.
    region: raw.region ? raw.region.trim().toUpperCase() : null
  }));
};

// ----------------------------------------------------
// 6. INGEST TRAINING SPECIALIST STRATEGICS (TSS)
// ----------------------------------------------------
export interface TssDictionaries {
  countries: Country[];
  redHatRegions: RedHatRegion[];
}

export const ingestTss = (rawTss: TrainingSpecialistStrategic[], dictionaries: TssDictionaries): TrainingSpecialistStrategic[] => {
  const countries = buildCacheMap(dictionaries.countries);
  const regions = buildCacheMap(dictionaries.redHatRegions);

  return rawTss.map(tss => {
    if (tss.country?.id) tss.country = countries.get(tss.country.id) || tss.country;
    if (tss.redHatRegion?.id) tss.redHatRegion = regions.get(tss.redHatRegion.id) || tss.redHatRegion;
    return tss;
  });
};
